import {EventEmitter, Injectable} from "@angular/core";
import {MmProjectLoaderService} from "../../mm-project/mm-project-loader.service";
import {filter, find, map, switchMap} from "rxjs/operators";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {AttributeGroup} from "@docma-mm-core/attribute-group/attribute-group";
import {LoadStatusEnum} from "@docma-mm-core/model/load-state/load-state.enum";

@Injectable()
@UntilDestroy()
export class AttributeService {

  private readonly imagesByAttributeId = new Map<number, {Url: string, Image: Blob}>();
  private readonly imagesByFileName = new Map<string, {Url: string, Image: Blob}>();
  private readonly attributeGroupsByAttributeId = new Map<number, AttributeGroup>();

  onLoaded = new EventEmitter();

  constructor(private readonly projectLoaderService: MmProjectLoaderService) {
    this.projectLoaderService.attributes.pipe(filter(a => a.loadState.status === LoadStatusEnum.Loaded))
      .pipe(switchMap(attributes => this.projectLoaderService.attributeGroups.pipe(filter(ag => ag.loadState.status === LoadStatusEnum.Loaded))
        .pipe(switchMap(attributeGroups => this.projectLoaderService.attributeGroupsFiles.pipe(filter(agf => agf.loadState.status === LoadStatusEnum.Loaded))
          .pipe(map(attributeGroupFiles => ({attributes, attributeGroupFiles, attributeGroups})
          ))
        ))
      ), untilDestroyed(this))
      .subscribe(async param => {
        if (param?.attributes) {
          this.imagesByAttributeId.clear();
          this.imagesByFileName.clear();
          this.attributeGroupsByAttributeId.clear();

          await Promise.all(param.attributes.data.map(async attribute => {
            const attributeGroupFile = param.attributeGroupFiles.data.find(x => x.Id === attribute.AttributeGroup.Id);
            const fileExtension = attributeGroupFile.ImageFileName?.split('.').pop()?.toLowerCase();
            let mimeType = 'image/png'; // Standard-MIME-Typ

            switch (fileExtension) {
              case 'gif':
                mimeType = 'image/gif';
                break;
              case 'tif':
              case 'tiff':
                mimeType = 'image/tiff';
                break;
              case 'jpg':
              case 'jpeg':
                mimeType = 'image/jpeg';
                break;
              case 'png':
                mimeType = 'image/png';
                break;
              case 'bmp':
                mimeType = 'image/bmp';
                break;
            }

            if (attributeGroupFile) {
              const blob = new Blob([attributeGroupFile.buffer], { type: mimeType });
              this.imagesByAttributeId.set(attribute.Id, {Url: URL.createObjectURL(blob), Image: blob});

              const attributeGroup = param.attributeGroups.data.find(x => x.Id === attribute.AttributeGroup.Id);
              if (attributeGroup && this.attributeGroupsByAttributeId.get(attribute.Id) === undefined) {
                this.attributeGroupsByAttributeId.set(attribute.Id, attributeGroup);
              }
            }
          }));

          await Promise.all(param.attributeGroupFiles.data.map(async attributeGroupFile => {
            const blob = new Blob([attributeGroupFile.buffer]);
            this.imagesByFileName.set(attributeGroupFile.ImageFileName, {Url: URL.createObjectURL(blob), Image: blob});
          }));

          this.onLoaded.emit();
        }
      });
  }

  getAttributeImage(attributeId: number): {Url: string, Image: Blob} {
    if (this.imagesByAttributeId.get(attributeId) !== undefined) {
      return this.imagesByAttributeId.get(attributeId);
    }
  }

  getAttributeGroup(attributeId: number) {
    if (this.attributeGroupsByAttributeId.get(attributeId) !== undefined) {
      return this.attributeGroupsByAttributeId.get(attributeId);
    }
  }

  getAttributeImageByFileName(fileName: string): {Url: string, Image: Blob} {
    if (this.imagesByFileName.get(fileName) !== undefined) {
      return this.imagesByFileName.get(fileName);
    }
  }

}
